import { useCallback } from 'react';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

export type LoomMeetingNudgeType = 'title' | 'sidenav';

export type LoomMeetingNudgeFlag = 'flagNotInterested' | 'flagRemindMe' | 'flagConnectCal';

export type LoomMeetingNudgeFlagDismissalMethod = 'close' | 'autoDismiss';

type LoomMeetingNudgeCtaByType = {
	title: 'connectCalendar' | 'more' | 'close' | 'notInterested' | 'undo';
	sidenav:
		| 'connectCalendar'
		| 'more'
		| 'notInterested'
		| 'remindMe'
		| 'undo'
		| 'whyAmISeeingThis'
		| 'dontForgetToConnectCalendar';
};

type LoomMeetingNudgeScreenByType = {
	title: 'notInterested';
	sidenav: 'notInterested' | 'remindMe' | 'dontForgetToConnectCalendar' | 'whyAmISeeingThis';
};

type LoomMeetingNudgeAnalytics<NudgeType extends LoomMeetingNudgeType> = {
	fireNudgeSeen: () => void;
	fireCtaClicked: (cta: LoomMeetingNudgeCtaByType[NudgeType]) => void;
	fireScreenSeen: (screenType: LoomMeetingNudgeScreenByType[NudgeType]) => void;
	fireFlagDismissed: (
		flag: LoomMeetingNudgeFlag,
		dismissalMethod?: LoomMeetingNudgeFlagDismissalMethod,
	) => void;
};

export const useLoomMeetingNudgeAnalytics = <NudgeType extends LoomMeetingNudgeType>(
	nudgeType: NudgeType,
): LoomMeetingNudgeAnalytics<NudgeType> => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	const fireNudgeSeen = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'loomMeetingNudge',
			attributes: { nudgeType },
		});
	}, [fireAnalyticsEvent, nudgeType]);

	const fireCtaClicked = useCallback(
		(cta: LoomMeetingNudgeCtaByType[NudgeType]) => {
			fireAnalyticsEvent({
				eventType: 'track',
				action: 'clicked',
				actionSubject: 'loomMeetingNudgeCTA',
				attributes: { nudgeType, cta },
			});
		},
		[fireAnalyticsEvent, nudgeType],
	);

	const fireScreenSeen = useCallback(
		(screenType: LoomMeetingNudgeScreenByType[NudgeType]) => {
			fireAnalyticsEvent({
				eventType: 'screen',
				name: 'loomMeetingNudgeScreen',
				attributes: { nudgeType, screenType },
			});
		},
		[fireAnalyticsEvent, nudgeType],
	);

	const fireFlagDismissed = useCallback(
		(flag: LoomMeetingNudgeFlag, dismissalMethod?: LoomMeetingNudgeFlagDismissalMethod) => {
			fireAnalyticsEvent({
				eventType: 'track',
				action: 'dismissed',
				actionSubject: 'loomMeetingNudgeFlag',
				attributes: {
					nudgeType,
					flag,
					...(dismissalMethod ? { dismissalMethod } : {}),
				},
			});
		},
		[fireAnalyticsEvent, nudgeType],
	);

	return { fireNudgeSeen, fireCtaClicked, fireScreenSeen, fireFlagDismissed };
};
